import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { INITIAL_FORM, ENERGY_CATEGORIES } from './wizardConstants';
import logger from '../../utils/logger';
import { API_URL } from '../../utils/apiConfig';
import { toNumber } from '../../utils/numbers';
import { useLanguage } from '../../context/LanguageContext';
import { useToast } from '../../context/ToastContext';
import { optimisticSave } from '../../utils/optimisticSave';

const enrichFactors = (factors) => factors.map(f => {
  const impacts = f.impacts || [];
  const scope3Val = impacts.filter(imp => imp.scope === 'scope3').reduce((sum, imp) => sum + (imp.value || 0), 0);
  const totalValue = scope3Val > 0
    ? scope3Val
    : impacts.filter(imp => imp.scope === 'scope1' || imp.scope === 'scope2').reduce((sum, imp) => sum + (imp.value || 0), 0);
  const unit = impacts[0]?.unit || f.default_unit || '';
  return { ...f, value: Math.round(totalValue * 1e6) / 1e6, unit };
});

/**
 * Migrate legacy product data (hardcoded fields) to entries[] format.
 */
const migrateToEntries = (phase, isUsage = false) => {
  if (!phase) return isUsage ? { entries: [], cycles_per_year: 1 } : { entries: [] };
  // Already in new format
  if (Array.isArray(phase.entries)) {
    return isUsage ? { entries: phase.entries, cycles_per_year: phase.cycles_per_year || 1 } : { entries: phase.entries };
  }
  // Legacy format → convert
  const entries = [];
  const mappings = [
    { cat: 'electricite', fid: 'electricity_factor_id', qty: isUsage ? 'electricity_kwh_per_cycle' : 'electricity_kwh', unit: 'kWh' },
    { cat: 'combustion_fixe', fid: 'fuel_factor_id', qty: isUsage ? 'fuel_kwh_per_cycle' : 'fuel_kwh', unit: 'kWh' },
    { cat: 'combustion_mobile', fid: 'carburant_factor_id', qty: isUsage ? 'carburant_l_per_cycle' : 'carburant_l', unit: 'l' },
    { cat: 'emissions_fugitives', fid: 'refrigerant_factor_id', qty: isUsage ? 'refrigerant_kg_per_cycle' : 'refrigerant_kg', unit: 'kg' },
  ];
  for (const m of mappings) {
    const factorId = phase[m.fid];
    const quantity = phase[m.qty] || 0;
    if (factorId || quantity > 0) {
      entries.push({
        _key: Math.random().toString(36).slice(2),
        category: m.cat,
        quantity,
        unit: m.unit,
        factor_id: factorId || '',
      });
    }
  }
  return isUsage ? { entries, cycles_per_year: phase.cycles_per_year || 1 } : { entries };
};


// 2026-06-26 — Bornes dures FE produit, source de vérité unique pour validateStep
// ET checkHardRanges. Alignement strict sur les bornes BE (models/__init__.py
// LIFESPAN_MAX=100 et CYCLES_PER_YEAR_MAX=10000). Ces constantes sont définies
// module-level pour garantir que validateStep (bouton Suivant) et checkHardRanges
// (boutons Enregistrer) bloquent / laissent passer EXACTEMENT les mêmes valeurs.
const LIFESPAN_MAX_FE = 100;
const CYCLES_MAX_FE = 10000;

export const useProductWizard = (isOpen, editingProduct, currentFiscalYear = null) => {
  const { t } = useLanguage();
  // Toast Lot 2 — migration ProductWizard vers le central. Les states locaux
  // errorToast/successToast + leurs setTimeout d'auto-dismiss sont supprimés ;
  // Radix gère duration (5000ms) côté Toast.jsx.
  const { showError, showSuccess } = useToast();
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [eolSearch, setEolSearch] = useState('');
  // ENH-01 Volet 2 — sentinel updated_at vivant (resync après chaque save pour
  // éviter 409 sur saves successifs). Initialisé depuis editingProduct au load,
  // muté avec la valeur retournée par PUT (_format_fy_version_as_product).
  const [currentUpdatedAt, setCurrentUpdatedAt] = useState(null);
  // ENH-01 Volet 2 fix UX — snapshot "propre" pour le dirty tracking en édition.
  // null = pas de snapshot (création OU pré-loadProductData) → fallback heuristique
  // historique (formData.name + end_of_life). En édition, capturé au loadProductData
  // puis resynchronisé après chaque save (stay ET final) avec formData courant.
  const [cleanSnapshot, setCleanSnapshot] = useState(null);

  // All energy factors grouped by category
  const [energyFactors, setEnergyFactors] = useState({});
  const [treatments, setTreatments] = useState([]);

  const [emissionsPreview, setEmissionsPreview] = useState({ transformation: 0, usage: 0, disposal: 0, total: 0 });

  const loadProductData = useCallback((product) => {
    const loaded = {
      name: product.name || '',
      description: product.description || '',
      product_type: product.product_type || 'finished',
      unit: product.unit || 'unit',
      lifespan_years: product.lifespan_years || 0,
      transformation: migrateToEntries(product.transformation, false),
      usage: migrateToEntries(product.usage, true),
      end_of_life: product.end_of_life || [],
    };
    setFormData(loaded);
    // ENH-01 Volet 2 — capture updated_at frais à l'ouverture en édition.
    setCurrentUpdatedAt(product?.updated_at || null);
    // ENH-01 Volet 2 fix UX — capture snapshot propre = état persisté au load.
    setCleanSnapshot(loaded);
  }, []);

  const loadEmissionFactors = useCallback(async () => {
    try {
      const catCodes = ENERGY_CATEGORIES.map(c => c.code);
      const promises = catCodes.map(code => axios.get(`${API_URL}/api/emission-factors/by-category/${code}`));
      promises.push(axios.get(`${API_URL}/api/emission-factors/by-category/fin_vie_produits`));

      const results = await Promise.all(promises);
      const grouped = {};
      catCodes.forEach((code, i) => { grouped[code] = enrichFactors(results[i].data || []); });
      setEnergyFactors(grouped);
      setTreatments(enrichFactors(results[catCodes.length].data || []));
    } catch (error) {
      logger.error('Failed to load emission factors:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadEmissionFactors();
      if (editingProduct) loadProductData(editingProduct);
    }
  }, [isOpen, editingProduct, loadEmissionFactors, loadProductData]);

  // --- Entry helpers for transformation/usage ---
  const addEntry = (phase, category) => {
    setFormData(prev => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        entries: [...prev[phase].entries, { _key: Math.random().toString(36).slice(2), category, quantity: 0, unit: '', factor_id: '' }],
      },
    }));
  };

  const updateEntry = (phase, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        entries: prev[phase].entries.map((e, i) => i === index ? { ...e, [field]: value } : e),
      },
    }));
  };

  const removeEntry = (phase, index) => {
    setFormData(prev => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        entries: prev[phase].entries.filter((_, i) => i !== index),
      },
    }));
  };

  // --- Emissions preview ---
  const calculateEmissionsPreview = useCallback(() => {
    let transformation = 0, usage = 0, disposal = 0;

    // 3.10 Transformation
    if (formData.product_type === 'semi_finished') {
      for (const entry of formData.transformation.entries) {
        const q = toNumber(entry.quantity);
        if (entry.factor_id && q > 0) {
          const factors = energyFactors[entry.category] || [];
          const f = factors.find(x => x.id === entry.factor_id);
          if (f) transformation += q * (f.value || 0);
        }
      }
    }

    // 3.11 Usage
    const totalCycles = formData.lifespan_years * (formData.usage.cycles_per_year || 1);
    for (const entry of formData.usage.entries) {
      const q = toNumber(entry.quantity);
      if (entry.factor_id && q > 0) {
        const factors = energyFactors[entry.category] || [];
        const f = factors.find(x => x.id === entry.factor_id);
        if (f) usage += q * totalCycles * (f.value || 0);
      }
    }

    // 3.12 End of life
    for (const entry of formData.end_of_life) {
      const q = toNumber(entry.quantity);
      if (entry.emission_factor_id && q > 0) {
        const f = treatments.find(t => t.id === entry.emission_factor_id);
        if (f) disposal += q * (f.value || 0);
      }
    }

    setEmissionsPreview({
      transformation: Math.round(transformation * 1000) / 1000,
      usage: Math.round(usage * 1000) / 1000,
      disposal: Math.round(disposal * 1000) / 1000,
      total: Math.round((transformation + usage + disposal) * 1000) / 1000,
    });
  }, [formData, treatments, energyFactors]);

  useEffect(() => { calculateEmissionsPreview(); }, [calculateEmissionsPreview]);

  // End of life helpers
  const addEndOfLifeEntry = () => setFormData(prev => ({ ...prev, end_of_life: [...prev.end_of_life, { _key: Math.random().toString(36).slice(2), name: '', quantity: 0, unit: 'kg', emission_factor_id: '' }] }));
  const updateEndOfLifeEntry = (index, field, value) => setFormData(prev => ({ ...prev, end_of_life: prev.end_of_life.map((e, i) => i === index ? { ...e, [field]: value } : e) }));
  const removeEndOfLifeEntry = (index) => setFormData(prev => ({ ...prev, end_of_life: prev.end_of_life.filter((_, i) => i !== index) }));

  // Steps
  const steps = [
    { number: 1, title: t('productWizard.steps.generalInfo') },
    { number: 2, title: t('productWizard.steps.transformation'), condition: formData.product_type === 'semi_finished' },
    { number: 3, title: t('productWizard.steps.usage') },
    { number: 4, title: t('productWizard.steps.endOfLife') },
    { number: 5, title: t('productWizard.steps.summary') },
  ].filter(s => s.condition !== false);

  const currentStepIndex = steps.findIndex(s => s.number === currentStep);
  const isLastStep = currentStepIndex === steps.length - 1;

  const validateStep = (stepNum) => {
    const errors = [];
    if (stepNum === 1) {
      if (!formData.name.trim()) errors.push(t('productWizard.errors.nameRequired'));
      // 2026-06-26 harmo bornes : > 30 → > LIFESPAN_MAX_FE (mur dur 100, aligné checkHardRanges).
      // Min 0.5 conservé inchangé (UX étape Informations, non requis au save).
      if (formData.lifespan_years < 0.5 || formData.lifespan_years > LIFESPAN_MAX_FE) errors.push(t('productWizard.errors.lifespanRange'));
    }
    if (stepNum === 3) {
      const cpy = Number(formData.usage?.cycles_per_year) || 0;
      if (cpy <= 0) errors.push(t('productWizard.errors.cyclesPositive'));
      // 2026-06-26 harmo bornes : ajout borne sup cycles (alignement checkHardRanges).
      if (cpy > CYCLES_MAX_FE) errors.push(t('productWizard.errors.cyclesMaxHard'));
    }
    if (stepNum === 4) {
      formData.end_of_life.forEach((e, i) => {
        if (!e.emission_factor_id) errors.push(t('productWizard.errors.lineFactor').replace('{n}', i + 1));
        if (toNumber(e.quantity) <= 0) errors.push(t('productWizard.errors.lineQuantity').replace('{n}', i + 1));
      });
    }
    return errors;
  };

  // Efface les erreurs dès que l'utilisateur modifie le formulaire, pour débloquer le bouton Suivant
  useEffect(() => { setValidationErrors([]); }, [formData]);

  const canGoNext = validationErrors.length === 0 && (currentStep === 1 ? formData.name.trim() !== '' : true);
  const goNext = () => { const e = validateStep(currentStep); if (e.length > 0) { setValidationErrors(e); return; } setValidationErrors([]); const ns = steps[currentStepIndex + 1]; if (ns) setCurrentStep(ns.number); };
  const goPrev = () => { setValidationErrors([]); const ps = steps[currentStepIndex - 1]; if (ps) setCurrentStep(ps.number); };
  const goToStep = (n) => { setValidationErrors([]); setCurrentStep(n); };

  // ENH-01 Volet 2 — factorisation : exécute PUT/POST + retourne la réponse
  // pour resync updated_at + propager succès au caller. Pas d'effet de bord
  // ouverture/fermeture wizard ici (handleSubmit vs handleSaveStay décident).
  const buildPayload = () => {
    const normalizeEntries = (phaseObj) => ({
      ...phaseObj,
      entries: (phaseObj?.entries || []).map(e => ({ ...e, quantity: toNumber(e.quantity) })),
    });
    const payload = {
      name: formData.name, description: formData.description, product_type: formData.product_type,
      unit: formData.unit, lifespan_years: formData.lifespan_years,
      transformation: formData.product_type === 'semi_finished' ? normalizeEntries(formData.transformation) : null,
      usage: normalizeEntries(formData.usage),
      end_of_life: formData.end_of_life.map(e => ({ ...e, quantity: toNumber(e.quantity) })),
    };
    payload.fiscal_year_id = currentFiscalYear?.id;
    return payload;
  };

  // 2026-06-26 audit lifespan_years 3e10 → 1.6e12 kgCO₂e. validateStep
  // (l.218-237) couvre [0.5, 30] mais n'est appelée que par goNext.
  // handleSaveStay / handleSubmit court-circuitaient. Hard cap = 100 ans /
  // 10000 cycles/an (alignement bornes BE LIFESPAN_MAX / CYCLES_PER_YEAR_MAX).
  // Validation ciblée pre-PUT (defense-in-depth ; le BE _validate_product_factors
  // est l'autorité, ce check FE évite le round-trip serveur sur cas évident).
  // 2026-06-26 hardening : Number(...) cast explicite — couvre les cas où la
  // valeur a transité par un input et a été stockée en string (NaN || 0 → 0,
  // donc on ne bloque pas faussement les undefined/empty).
  const checkHardRanges = () => {
    // 2026-06-26 : utilise les constantes module-level partagées avec validateStep.
    const lifespan = Number(formData.lifespan_years) || 0;
    if (lifespan > LIFESPAN_MAX_FE) {
      showError(t('productWizard.errors.lifespanMaxHard'), { title: t('productWizard.dialogs.saveErrorTitle') });
      return false;
    }
    const cpy = Number(formData.usage?.cycles_per_year) || 0;
    if (cpy > CYCLES_MAX_FE) {
      showError(t('productWizard.errors.cyclesMaxHard'), { title: t('productWizard.dialogs.saveErrorTitle') });
      return false;
    }
    return true;
  };

  // 2026-06-26 catch handler robuste — gère 3 formats de 422 :
  //   (a) { detail: { errors: ["msg FR", ...] } }   — _validate_product_factors
  //       (route /products/enhanced POST+PUT, format métier maison).
  //   (b) { detail: "msg" }                          — HTTPException string simple.
  //   (c) { detail: [ {loc, msg, type, ...} ] }     — Pydantic 422 standard,
  //       déclenché par Field(le=...) AVANT le handler. Map vers nos clés i18n
  //       FR pour les bornes connues (lifespan_years, cycles_per_year). Sinon
  //       concatène les msg natifs. Filet ultime si checkHardRanges() est
  //       contourné (cas edge race state, payload tiers, etc.) → l'utilisateur
  //       voit un message FR explicite au lieu du générique.
  const parseErrorDetail = (detail) => {
    if (detail?.errors && Array.isArray(detail.errors)) {
      return detail.errors.join('\n');
    }
    if (typeof detail === 'string') {
      return detail;
    }
    if (Array.isArray(detail)) {
      return detail.map(e => {
        const loc = Array.isArray(e?.loc) ? e.loc.join('.') : '';
        if (e?.type === 'less_than_equal' && loc.includes('lifespan_years')) {
          return t('productWizard.errors.lifespanMaxHard');
        }
        if (e?.type === 'less_than_equal' && loc.includes('cycles_per_year')) {
          return t('productWizard.errors.cyclesMaxHard');
        }
        return e?.msg || t('productWizard.errors.generic');
      }).join('\n');
    }
    return t('productWizard.errors.generic');
  };

  const handleSaveStay = async (onProductCreated) => {
    // ENH-01 Volet 2 — édition seule. Save anticipé sans fermer le wizard.
    // Réutilise PUT /products/enhanced/{id} avec sentinel updated_at vivant.
    if (!editingProduct) return;
    if (!checkHardRanges()) return;  // 2026-06-26 fix bug lifespan/cycles
    setLoading(true);
    try {
      const payload = buildPayload();
      const result = await optimisticSave(t, () =>
        axios.put(`${API_URL}/api/products/enhanced/${editingProduct.id}`, {
          ...payload,
          updated_at: currentUpdatedAt,
        })
      );
      if (!result.ok) {
        onProductCreated?.();  // refresh data (modal 409 géré par optimisticSave)
        return;
      }
      // Resync sentinel depuis la réponse PUT (_format_fy_version_as_product
      // expose updated_at frais via backend l.680). Évite 409 sur saves successifs.
      const fresh = result.data?.updated_at;
      if (fresh) setCurrentUpdatedAt(fresh);
      // ENH-01 Volet 2 fix UX — resync snapshot avec formData courant (= état
      // qui vient d'être persisté) → croix ne déclenchera plus le confirm
      // "Quitter sans sauvegarder" tant que rien n'a été modifié depuis ce save.
      // Une nouvelle édition rendra à nouveau formData ≠ cleanSnapshot → confirm
      // réapparaît (invariant préservé). On capture formData (pas payload) pour
      // matcher la comparaison JSON.stringify(formData) vs cleanSnapshot.
      setCleanSnapshot(formData);
      onProductCreated?.();
      showSuccess(t('productWizard.toasts.savedProgress'));
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = parseErrorDetail(detail);
      showError(msg, { title: t('productWizard.dialogs.saveErrorTitle') });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (onProductCreated, onClose) => {
    // Phase 1.1.D.1 — fiscal_year_id is mandatory on create. Update path keeps
    // its previous behaviour (handled in a later phase).
    if (!editingProduct && !currentFiscalYear?.id) {
      showError(t('productWizard.errors.fiscalYearRequired'), { title: t('productWizard.dialogs.saveErrorTitle') });
      return;
    }
    if (!checkHardRanges()) return;  // 2026-06-26 fix bug lifespan/cycles
    setLoading(true);
    try {
      const payload = buildPayload();
      if (editingProduct) {
        const result = await optimisticSave(t, () =>
          axios.put(`${API_URL}/api/products/enhanced/${editingProduct.id}`, {
            ...payload,
            updated_at: currentUpdatedAt,
          })
        );
        if (!result.ok) {
          onProductCreated?.();  // refresh data
          return;
        }
        const fresh = result.data?.updated_at;
        if (fresh) setCurrentUpdatedAt(fresh);
      } else {
        await axios.post(`${API_URL}/api/products/enhanced`, payload);
      }
      onProductCreated?.();
      resetAndClose(onClose);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = parseErrorDetail(detail);
      showError(msg, { title: t('productWizard.dialogs.saveErrorTitle') });
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = (onClose) => {
    setCurrentStep(1); setShowCloseConfirm(false); setEolSearch('');
    setFormData({ ...INITIAL_FORM, transformation: { entries: [] }, usage: { entries: [], cycles_per_year: 1 }, end_of_life: [] });
    onClose();
  };

  // ENH-01 Volet 2 fix UX — dirty tracking via snapshot en édition (resync au load
  // et après chaque save). Fallback heuristique historique si snapshot null (création
  // OU pré-load) → rétrocompat parfaite avec le comportement antérieur.
  const hasUnsavedChanges = () => {
    if (cleanSnapshot) {
      return JSON.stringify(formData) !== JSON.stringify(cleanSnapshot);
    }
    return formData.name.trim() !== '' || formData.end_of_life.length > 0;
  };

  const filteredTreatments = eolSearch.trim()
    ? treatments.filter(t => {
        const label = (t.name_simple_fr || t.source_product_name || t.name || '').toLowerCase();
        const tags = (t.search_tags || []).join(' ').toLowerCase();
        const q = eolSearch.toLowerCase();
        return label.includes(q) || tags.includes(q);
      })
    : treatments;

  return {
    formData, setFormData, loading, validationErrors,
    currentStep, showCloseConfirm, setShowCloseConfirm, eolSearch, setEolSearch,
    emissionsPreview, steps, currentStepIndex, isLastStep, canGoNext,
    goNext, goPrev, goToStep, handleSubmit, handleSaveStay, resetAndClose, hasUnsavedChanges,
    addEntry, updateEntry, removeEntry,
    addEndOfLifeEntry, updateEndOfLifeEntry, removeEndOfLifeEntry,
    energyFactors, treatments, filteredTreatments,
  };
};
